// src/pages/AddLeadPage.js
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  ListItemText,
  Typography,
  Tabs,
  Tab,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Modal,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import UnifiedPageWrapper from "../components/Layout/UnifiedPageWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import API from "../utils/api";
import { toast } from "react-toastify";
import { emitLeadUpdate, subscribeLeadUpdates } from "../utils/leadSync";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import { LeadStatusSelectMenuItems } from "../components/LeadStatusMenuItems";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";


export default function AddLeadPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const territoryId = searchParams.get("territory_id");

  const [tab, setTab] = useState(0);
  const [territory, setTerritory] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [file, setFile] = useState(null);
  const [selectedReps, setSelectedReps] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Import summary state
  const [importSummary, setImportSummary] = useState(null);
  const [showSkippedDetails, setShowSkippedDetails] = useState(false);
 

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    status: "New",
    assigned_rep: "",
    latitude: "",
    longitude: "",
    territory: territoryId || "",
    notes: "",
    additional_notes: "",
    sold_date: "",
    property_type: "",
    price: "",
    square_feet: "",
    year_built: "",
  });

  const { can, isReadOnly, role } = useAuth();
  const canEditTerritoryName = role === "Admin" && !isReadOnly;
  const [confirmDeleteTerritory, setConfirmDeleteTerritory] = useState(false);

  // helper to normalize manager to an id
  const getManagerId = (mgr) =>
    mgr && typeof mgr === "object" ? mgr.id : mgr || null;

  const fetchData = async () => {
    try {
      if (!territoryId) return;
      const [territoryRes, leadsRes, usersRes] = await Promise.all([
        API.get(`/territories/${territoryId}/`),
        API.get(`/leads/?territory=${territoryId}`),
        API.get("/users/"),
      ]);
      // normalize manager to an id for stable Select value
      const t = territoryRes.data || null;
      const normalized = t
        ? { ...t, manager: getManagerId(t.manager) }
        : null;

      setTerritory(normalized);
      setLeads(leadsRes.data);
      setUsers(usersRes.data);

      // Pre-populate selected reps from territory
    if (normalized?.assigned_reps) {
      setSelectedReps(normalized.assigned_reps.map(r => r.id));
    }
    } catch (err) {
      console.error("Error fetching add-lead data:", err);
      toast.error("Failed to load territory or leads");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId]);

  const handleFileUpload = async () => {
    if (!file) return toast.error("Please select a file first.");
    
    // Ensure territory ID is always passed when available from URL
    if (!territoryId) {
      toast.error("Territory ID is missing. Please navigate from a territory page.");
      return;
    }
    
    const data = new FormData();
    data.append("file", file);
    data.append("territory", territoryId); // Always append when territoryId exists

    setIsUploading(true); // Start loading

    try {
      const response = await API.post("/import-leads/", data, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 minutes for large file uploads (processing + geocoding can take time)
      });

      const result = response.data;
      
      if (result.error) {
        toast.error(`❌ Upload failed: ${result.error}`);
        setIsUploading(false);
        return;
      }

      const hasCreated = (result.created ?? 0) > 0;
      const hasEnriched = (result.enriched ?? 0) > 0;
      const hasConflicted = (result.conflicted ?? 0) > 0;
      const hasSkipped = (result.skipped ?? 0) > 0;

      const partialNote = result.partial
        ? ` Stopped early (${result.stopped_reason || "time_budget"}): ${result.rows_remaining ?? 0} data rows not processed — re-upload to continue (dedupe applies).`
        : "";

      if (hasCreated || hasEnriched) {
        const parts = [];
        if (hasCreated) parts.push(`${result.created} created`);
        if (hasEnriched) parts.push(`${result.enriched} enriched`);
        if (hasConflicted) parts.push(`${result.conflicted} conflicted`);
        if (hasSkipped) parts.push(`${result.skipped} skipped`);
        toast.success(`✅ Import complete: ${parts.join(", ")}.${partialNote}`);
        setFile(null);
        await refreshLeads();

        const updatedLeadsRes = await API.get(territoryId ? `/leads/?territory=${territoryId}` : '/leads/');
        const updatedLeadsCount = updatedLeadsRes.data?.length || 0;

        setImportSummary({
          uploaded: result.created ?? 0,
          enriched: result.enriched ?? 0,
          conflicted: result.conflicted ?? 0,
          unchanged: result.unchanged ?? 0,
          skipped: result.skipped ?? 0,
          totalInTerritory: updatedLeadsCount,
          errors: result.errors || [],
        });
        setShowSkippedDetails(false);
      } else if (hasSkipped || hasConflicted) {
        const parts = [];
        if (hasConflicted) parts.push(`${result.conflicted} conflicted`);
        if (hasSkipped) parts.push(`${result.skipped} skipped`);
        toast.warning(`⚠️ No new leads created. ${parts.join(", ")}.${partialNote}`);
        if (result.errors && result.errors.length > 0) {
          console.error("Upload errors:", result.errors);
        }
        const currentLeadsRes = await API.get(territoryId ? `/leads/?territory=${territoryId}` : '/leads/');
        const currentLeadsCount = currentLeadsRes.data?.length || 0;

        setImportSummary({
          uploaded: 0,
          enriched: result.enriched ?? 0,
          conflicted: result.conflicted ?? 0,
          unchanged: result.unchanged ?? 0,
          skipped: result.skipped ?? 0,
          totalInTerritory: currentLeadsCount,
          errors: result.errors || [],
        });
        setShowSkippedDetails(false);
      } else if (result.partial) {
        toast.warning(
          `Import stopped early (${result.stopped_reason || "time_budget"}). ${result.rows_processed ?? 0} of ${result.total_rows ?? "?"} data rows processed; ${result.rows_remaining ?? 0} not processed — re-upload to continue (dedupe applies).`
        );
        setFile(null);
        await refreshLeads();
        const currentLeadsRes = await API.get(territoryId ? `/leads/?territory=${territoryId}` : "/leads/");
        const currentLeadsCount = currentLeadsRes.data?.length || 0;
        setImportSummary({
          uploaded: result.created ?? 0,
          enriched: result.enriched ?? 0,
          conflicted: result.conflicted ?? 0,
          unchanged: result.unchanged ?? 0,
          skipped: result.skipped ?? 0,
          totalInTerritory: currentLeadsCount,
          errors: result.errors || [],
        });
        setShowSkippedDetails(false);
      } else {
        toast.warning("⚠️ No leads were created. Please check your file format.");
        setImportSummary(null);
      }
    } catch (err) {
      console.error("Upload error:", err);
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || err.message || "Upload failed";
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setIsUploading(false); // Always stop loading
    }
  };

  const handleSaveLead = async () => {
    const hasCoords = formData.latitude && formData.longitude;
    const hasAddress = formData.address && formData.city && formData.state;
    if (!hasCoords && !hasAddress) {
      toast.warning("⚠️ Please provide either Latitude & Longitude or a full address (Street, City, State).");
      return;
    }

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        status: formData.status,
        assigned_rep: formData.assigned_rep || null,
        latitude: hasCoords ? parseFloat(formData.latitude) : null,
        longitude: hasCoords ? parseFloat(formData.longitude) : null,
        territory: territoryId || formData.territory || null,
	      notes: formData.notes || "",
        sold_date: formData.sold_date || null,
	property_type: formData.property_type || "",
	price: formData.price || null,
	square_feet: formData.square_feet || null,
	year_built: formData.year_built || null,
      };

      const response = await API.post("/leads/", payload);
      toast.success("Lead added successfully!");
      emitLeadUpdate(response.data);
      await refreshLeads();
      setFormData({
        first_name: "",
        last_name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        status: "New",
        assigned_rep: "",
        latitude: "",
        longitude: "",
        territory: territoryId || "",
        notes: "",
        sold_date: "",
        property_type: "",
        price: "",
        square_feet: "",
        year_built: "",
      });
    } catch (err) {
      console.error("Error saving lead:", err);
      toast.error("Failed to add lead");
    }
  };

  const refreshLeads = useCallback(async () => {
    try {
      // Build URL properly - only add territory parameter if it exists
      const url = territoryId 
        ? `/leads/?territory=${territoryId}`
        : '/leads/';
      const res = await API.get(url);
      setLeads(res.data);
    } catch (err) {
      console.error("Error refreshing leads:", err);
    }
  }, [territoryId]);

  useEffect(() => {
    const unsubscribe = subscribeLeadUpdates(() => {
      setTimeout(() => refreshLeads(), 300);
    });
    return () => unsubscribe();
  }, [refreshLeads]);

  const handleCancel = () => navigate("/territories");

  // ============= CONTAINER 1 handlers (fixed) =============
  const handleManagerChange = (e) => {
    const managerId = e.target.value || null;
    setTerritory((prev) => ({ ...prev, manager: managerId }));
  };

  const handleTerritoryNameChange = (e) => {
    const v = e.target.value;
    setTerritory((prev) => (prev ? { ...prev, name: v } : prev));
  };

  const handleSaveTerritory = async () => {
    try {
      if (!territory) return;
      await API.put(`/territories/${territory.id}/`, {
        name: territory.name,
        // IMPORTANT: serializer expects `manager`, not `manager_id`
        manager: getManagerId(territory.manager),
        assigned_reps_ids: selectedReps,
      });
      await API.post(`/territories/${territory.id}/sync_leads/`);
      await fetchData();
      await refreshLeads();
  

      toast.success("Territory updated successfully!");
      // refresh local view so the selected manager shows immediately
      await fetchData();
      setSelectedReps([]);
    } catch (err) {
      console.error("Error updating territory:", err);
      toast.error("Failed to update territory");
    }
  };

  const handleDeleteTerritory = () => {
    if (!territory?.id) return;
    setConfirmDeleteTerritory(true);
  };

  const handleConfirmDeleteTerritory = async () => {
    try {
      await API.delete(`/territories/${territory.id}/`);
      toast.success("Territory deleted successfully");
      navigate("/territories");
    } catch (err) {
      console.error("Error deleting territory:", err);
      toast.error("Failed to delete territory");
    } finally {
      setConfirmDeleteTerritory(false);
    }
  };

  // ============= Container 2 handlers (restored) =============
  // Handle view leads navigation
  const handleViewLeads = () => {
    if (territoryId) {
      navigate(`/leads/search?territory=${territoryId}`);
    } else {
      navigate("/leads/search");
    }
  };

  const timestamp = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const handleDownloadSkippedCSV = () => {
    if (!importSummary?.errors?.length) return;
    const header = "Row,Address,Reason\n";
    const escapeCsv = (v) => {
      const s = String(v ?? "").replace(/"/g, '""');
      return s.includes(",") || s.includes("\n") || s.includes('"') ? `"${s}"` : s;
    };
    const rows = importSummary.errors.map(
      (e) => `${e.row},${escapeCsv(e.address ?? "—")},${escapeCsv(e.reason)}`
    );
    const blob = new Blob(["\uFEFF" + header + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-import-skipped-${timestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadSkippedTXT = () => {
    if (!importSummary?.errors?.length) return;
    const lines = ["Row\tAddress\tReason", ...importSummary.errors.map((e) => `${e.row}\t${(e.address ?? "—").replace(/\t/g, " ")}\t${(e.reason ?? "").replace(/\t/g, " ")}`)];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-import-skipped-${timestamp()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 5,
        minHeight: "100vh",
        p: 3,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      {/* ====================== CONTAINER 1 - Territory Info ====================== */}
      <Paper
        data-tour="territory-edit-container"
        elevation={3}
        sx={{
          p: 4,
          width: { xs: "100%", md: "80%" },
          maxWidth: { xs: "100%", md: "600px" },
          borderRadius: "16px",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          mt: 4,
        }}
      >
      <Box display="flex" flexDirection="column" mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Edit Territory
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => navigate("/territories")}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              whiteSpace: "nowrap",
              flexShrink: 0,
              ml: 1,
            }}
          >
            ← Back to Territories
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {territory
            ? canEditTerritoryName
              ? `Edit territory name and/or assign a manager for "${territory.name}".`
              : `Assign new manager to "${territory.name}" territory.`
            : "Loading territory information..."}
        </Typography>
      </Box>


        <Divider sx={{ mb: 3, borderColor: theme.palette.divider }} />

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Territory Name"
            value={territory?.name || ""}
            variant="outlined"
            disabled={!canEditTerritoryName}
            onChange={handleTerritoryNameChange}
            helperText={
              canEditTerritoryName
                ? "Admin only: the new name appears everywhere this territory is shown."
                : "Only an Admin can change the territory name."
            }
          />

          <FormControl fullWidth>
            <InputLabel>Manager</InputLabel>
            <Select
              label="Manager"
              // handle both object and id safely
              value={
                territory
                  ? getManagerId(territory.manager) ?? ""
                  : ""
              }
              onChange={handleManagerChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          <TextField
            type="number"
            fullWidth
            label="Lead Count"
            value={leads.length}
            variant="outlined"
            inputProps={{ min: 0 }}
            disabled
          />
        </Stack>

        <Box
  display="flex"
  justifyContent="space-between"
  gap={2}
  mt={4}
  pt={2}
  
  sx={{
    
    flexDirection: { xs: "column-reverse", md: "row" },
    alignItems: { xs: "stretch", md: "center" },
  }}
>

          {can.canDelete && territory?.id && (
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteTerritory}
              disabled={isReadOnly}
              sx={{ flex: 1 }}
            >
              Delete
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            sx={{ flex: can.canDelete && territory?.id ? 1 : "48%" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveTerritory}
            disabled={isReadOnly}
            sx={{ flex: can.canDelete && territory?.id ? 1 : "48%" }}
          >
            Save Territory
          </Button>
        </Box>
      </Paper>
{/* ====================== CONTAINER 2 – LEADS TABLE & ADD FORMS ====================== */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: "1200px",
          borderRadius: "16px",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          mb: 6,
        }}
      >
        {/* ---------- Instructions at TOP ---------- */}
        <Box sx={{ mb: 3 }} data-tour="bulk-upload-leads-container">
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              fontSize: { xs: "1rem", md: "1.125rem" },
            }}
          >
            Bulk Upload Leads
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              lineHeight: 1.6,
            }}
          >
            Upload a CSV or Excel file (.csv, .xlsx, .xls) containing your leads.
            The system will automatically geocode addresses, validate data, and
            import leads into the selected territory. After upload, you'll see a
            summary of uploaded, skipped, and total leads.
          </Typography>
        </Box>

        {/* ------------------------------Import Summary Section------------------------ */}
        {importSummary && (
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.success.main, 0.05),
              border: `2px solid ${theme.palette.success.main}`,
              borderRadius: "12px",
              px: 3,
              py: 2.5,
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "success.main",
                fontWeight: 700,
                letterSpacing: 0.3,
                mb: 2,
              }}
            >
              Import Summary
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Leads Created</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: "success.main" }}
                      >
                        {importSummary.uploaded ?? 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {(importSummary.enriched ?? 0) > 0 && (
                    <TableRow>
                      <TableCell>Enriched</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "info.main" }}
                        >
                          {importSummary.enriched}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(importSummary.conflicted ?? 0) > 0 && (
                    <TableRow>
                      <TableCell>Conflicted</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "warning.main" }}
                        >
                          {importSummary.conflicted}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(importSummary.skipped ?? 0) > 0 && (
                    <TableRow>
                      <TableCell>Skipped</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "text.secondary" }}
                        >
                          {importSummary.skipped}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>Total Leads in Territory</TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {importSummary.totalInTerritory}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {importSummary.skipped > 0 && importSummary.errors?.length > 0 && (() => {
              const byReason = (importSummary.errors || []).reduce((acc, e) => {
                const r = e.reason ?? "Unknown";
                acc[r] = (acc[r] || 0) + 1;
                return acc;
              }, {});
              return (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "warning.dark" }}>
                    Grouped by reason
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5, listStyle: "none" }}>
                    {Object.entries(byReason).map(([reason, count]) => (
                      <Typography key={reason} component="li" variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{count}</strong> row{count !== 1 ? "s" : ""} skipped due to <strong>{reason}</strong>
                      </Typography>
                    ))}
                  </Stack>
                  <Button
                    size="small"
                    fullWidth
                    onClick={() => setShowSkippedDetails((prev) => !prev)}
                    endIcon={showSkippedDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mt: 1.5, justifyContent: "space-between" }}
                  >
                    {showSkippedDetails ? "Hide" : "Show"} skipped details
                  </Button>
                  <Collapse in={showSkippedDetails} timeout="auto" unmountOnExit>
                    <TableContainer sx={{ maxHeight: 280, border: "1px solid", borderColor: "divider", borderRadius: 1, mt: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Row</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Address</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importSummary.errors.map((e, i) => (
                            <TableRow key={i}>
                              <TableCell>{e.row}</TableCell>
                              <TableCell sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>{e.address ?? "—"}</TableCell>
                              <TableCell>{e.reason ?? ""}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadSkippedCSV}
                      >
                        Download as CSV
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadSkippedTXT}
                      >
                        Download as TXT
                      </Button>
                    </Stack>
                  </Collapse>
                </Box>
              );
            })()}
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<VisibilityIcon />}
                onClick={handleViewLeads}
                sx={{ minWidth: 150 }}
              >
                View Leads
              </Button>
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        <Tabs
          value={tab}
          onChange={(_, newVal) => setTab(newVal)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            mb: 3,
            "& .MuiTabs-flexContainer": {
              flexDirection: { xs: "column", md: "row" },
            },
          }}
        >
          <Tab
            data-tour="upload-leads-button"
            icon={<UploadFileIcon />}
            label="Upload Leads from File"
            sx={{
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              width: { xs: "100%", md: "auto" },
              minWidth: { xs: "100%", md: 160 },
              borderBottom: {
                xs: tab === 0 ? "3px solid #1976d2" : "3px solid transparent",
                md: "none",
              },
            }}
          />

          <Tab
            data-tour="add-leads-button"
            icon={<PersonAddIcon />}
            label="Add Individual Lead"
            sx={{
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              width: { xs: "100%", md: "auto" },
              minWidth: { xs: "100%", md: 160 },
              borderBottom: {
                xs: tab === 1 ? "3px solid #1976d2" : "3px solid transparent",
                md: "none",
              },
            }}
          />
        </Tabs>
        
        {tab === 0 && (
          <Box sx={{ maxWidth: 720, mx: "auto" }}>
            {/* ---------- Actions ---------- */}
            <Box sx={{ textAlign: "center" }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
		justifyContent="center"
                sx={{ mb: 3 }}
              >
                <Button
                  data-tour="choose-file-button"
                  variant="contained"
                  component="label"
                  size="small"
                  disabled={isReadOnly || isUploading}
                >
                  Choose File
                  <input
                    type="file"
                    accept=".csv, .xlsx, .xls"
                    hidden
                    disabled={isReadOnly || isUploading}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      setFile(e.target.files[0]);
                      setImportSummary(null);
                    }}
                  />
                </Button>

                {file && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      wordBreak: "break-word",
                    }}
                  >
                    {file.name}
                  </Typography>
                )}
              </Stack>

              <Button
                data-tour="upload-file-button"
                variant="contained"
                color="primary"
                onClick={handleFileUpload}
                disabled={isReadOnly || !file || isUploading}
                startIcon={
                  isUploading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                size="small"
              >
                {isUploading ? "Uploading..." : "Upload File"}
              </Button>
            </Box>
          </Box>
        )}
      


        {tab === 1 && (
          <Box>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="First Name"
                  name="first_name"
                  fullWidth
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                />
                <TextField
                  label="Last Name"
                  name="last_name"
                  fullWidth
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                />
              </Stack>

              <TextField
                label="Address"
                name="address"
                fullWidth
                value={formData.address || ""}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  label="City"
                  name="city"
                  fullWidth
                  value={formData.city || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
                <TextField
                  label="State"
                  name="state"
                  fullWidth
                  value={formData.state || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                />
                <TextField
                  label="ZIP Code"
                  name="zip_code"
                  fullWidth
                  value={formData.zip_code || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, zip_code: e.target.value })
                  }
                />
              </Stack>

		<Stack direction="row" spacing={2}>
  <TextField
    label="Sold Date"
    type="date"
    value={formData.sold_date}
    onChange={(e) =>
      setFormData({ ...formData, sold_date: e.target.value })
    }
    InputLabelProps={{ shrink: true }}
    fullWidth
  />

  <TextField
    label="Property Type"
    value={formData.property_type}
    onChange={(e) =>
      setFormData({ ...formData, property_type: e.target.value })
    }
    fullWidth
  />
</Stack>

<Stack direction="row" spacing={2}>
  <TextField
    label="Price"
    type="number"
    value={formData.price}
    onChange={(e) =>
      setFormData({ ...formData, price: e.target.value })
    }
    fullWidth
  />

  <TextField
    label="Square Feet"
    type="number"
    value={formData.square_feet}
    onChange={(e) =>
      setFormData({ ...formData, square_feet: e.target.value })
    }
    fullWidth
  />

  <TextField
    label="Year Built"
    type="number"
    value={formData.year_built}
    onChange={(e) =>
      setFormData({ ...formData, year_built: e.target.value })
    }
    fullWidth
  />
</Stack>


              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <LeadStatusSelectMenuItems />
                  </Select>
                </FormControl>
		{/* --------------Notes Field — moved here--------------- */}
<TextField
  label="Notes"
  name="notes"
  value={formData.notes || ""}
  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
  fullWidth
  multiline
  minRows={1}     // 🔽 reduced for consistent height
  maxRows={3}     // ✅ expands only when needed
  sx={{ mb: 2 }}
/>


              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Latitude (REQUIRED)"
                  name="latitude"
                  fullWidth
                  required
                  value={formData.latitude}
                  onChange={(e) =>
                    setFormData({ ...formData, latitude: e.target.value })
                  }
                />
                <TextField
                  label="Longitude (REQUIRED)"
                  name="longitude"
                  fullWidth
                  required
                  value={formData.longitude}
                  onChange={(e) =>
                    setFormData({ ...formData, longitude: e.target.value })
                  }
                />
              </Stack>

              <Stack 
                direction={{ xs: "column", md: "row" }} 
                spacing={2} 
                justifyContent={{ xs: "stretch", md: "flex-end" }}
                sx={{ width: "100%" }}
              >
                <Button
                  variant="outlined"
                  onClick={() =>
                    setFormData({
                      first_name: "",
                      last_name: "",
                      address: "",
                      city: "",
                      state: "",
                      zip_code: "",
                      status: "New",
                      assigned_rep: "",
                      latitude: "",
                      longitude: "",
                      territory: territoryId || "",
                      notes: "",
                      sold_date: "",
                      property_type: "",
                      price: "",
                      square_feet: "",
                      year_built: "",
                    })
                  }
                  size="small"
                  sx={{ 
                    textTransform: "none", 
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    width: { xs: "100%", md: "auto" }
                  }}
                >
                  Clear Form
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveLead}
                  size="small"
                  sx={{ 
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    width: { xs: "100%", md: "auto" }
                  }}
                >
                  Save Lead
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

       
      </Paper>

      {/* Confirm Delete Territory Dialog */}
      <ConfirmDialog
        open={confirmDeleteTerritory}
        onClose={() => setConfirmDeleteTerritory(false)}
        onConfirm={handleConfirmDeleteTerritory}
        title="Delete Territory"
        message="Are you sure you want to delete this territory? All associated leads will be unassigned. This action cannot be undone."
        itemName={territory?.name ? `Territory: ${territory.name}` : null}
      />
    </Box>
  );
}